import { useCallback, useEffect, useState } from 'react'

import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { ReportBlockAdd, ReportBlockCustomText, ReportBlockBarChart, ReportBlockComments, ReportBlockBroken, ConfiglessBlocks, GridBlocksEnum, GridBlockTitles } from './blocks'

import { ReactComponent as DeleteIcon } from '../assets/bin-2.svg'
import { ReactComponent as ThreeDots } from '../assets/three-dots.svg'
import { ReactComponent as DuplicateIcon } from '../assets/duplicate.svg'
import { ReactComponent as VisibleIcon } from '../assets/visibility_on.svg'
import { ReactComponent as VisibleOffIcon } from '../assets/visibility_off.svg'
import { ReactComponent as Accordion } from '../assets/accordion.svg'
import { ReactComponent as PencilIcon } from 'assets/pencil-icon.svg'

import '../../utils/font-family.scss'
import './ReportGrid.scss'

import { useModalControls } from '../Modal.jsx'
import ReportEditModal from './ReportEditModal.jsx'
import Tooltip from './Tooltip.jsx'

/**
 * A hook for handling the report grid list.
 * @returns
 */
export const useReportGridList = (initialItems = []) => {
  /**
   * Block list.
   *
   * example block
   * {
   *  id: '123',
   *  title: 'Title', // This is shown on the title bar of a block.
   *  config: {}, // This is how the block is configured
   * }
   */
  const [items, setItems] = useState(initialItems)

  const addIdToBlock = (item) => ({
    ...item,
    id: item.id ?? crypto.randomUUID()
  })

  const addItem = useCallback((item) => {
    /**
     * Here we use created to mark when an item has been added to the list and any further changes to the block will be an update operation
     * we can no longer use Id as it is added before submission
     */
    const newBlock = {
      ...addIdToBlock(item),
      created: true
    }

    setItems((prev) => [...prev, newBlock])
    return newBlock
  }, [])

  const removeItem = useCallback((itemId) => {
    setItems((prev) => prev.filter(({ id }) => id !== itemId))
  }, [])

  const updateBlock = useCallback((item) => {
    setItems((prev) => prev.map((existing) => (existing.id === item.id ? { ...existing, ...item } : existing)))
  }, [])

  /**
   * Updates the current order to be used with muuri's order handler.
   * @param {Array<string>} ids
   */
  const updateOrder = useCallback((ids) => {
    const indexMap = new Map(ids.map((id, i) => [id, i]))

    setItems((prev) => [...prev].sort((a, b) => indexMap.get(a.id) - indexMap.get(b.id)))
  }, [])

  return {
    items,
    setItems,
    addItem,
    removeItem,
    updateOrder,
    updateBlock,
    addIdToBlock
  }
}

const Item = ({ id, type, config = {}, removeItem = () => {}, handleEditItem = () => {}, updateBlock = () => {}, item = {}, defaultViewing, dragging }) => {
  const [viewContent, setViewContent] = useState(defaultViewing)
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
  const [name, setName] = useState(item?.config?.title ? ` - ${item?.config?.title}` : '')
  const [showMoreOptions, setShowMoreOptions] = useState(false)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const GridBlocksMap = {
    [GridBlocksEnum.CUSTOM_TEXT]: ReportBlockCustomText,
    [GridBlocksEnum.COMMENTS]: ReportBlockComments,
    [GridBlocksEnum.CUSTOM_CHART]: ReportBlockBarChart,
    [GridBlocksEnum.BLOCK_ADD]: ReportBlockAdd
  }

  const title = GridBlockTitles ? GridBlockTitles[type] : false

  const BlockComponent = type && GridBlocksMap?.[type] ? GridBlocksMap[type] : ReportBlockBroken

  useEffect(() => {
    setName(item?.config?.title ? ` - ${item?.config?.title}` : '')
  }, [item, item.config])

  return (
    <div className='item' ref={setNodeRef} style={style}>
      <div className='item-content' style={{ opacity: dragging ? 0.2 : 1 }}>
        {(title && (
          <div className='title'>
            <button className={viewContent ? 'accordion' : 'accordion open'} onClick={() => setViewContent((s) => !s)}>
              <Accordion />
            </button>
            <div className='text move-cursor' {...attributes} {...listeners}>
              <h3>
                {title}
                {name}
              </h3>
            </div>
            <div className='title-actions'>
              <button
                onClick={() =>
                  updateBlock({
                    ...item,
                    visible: !item.visible
                  })
                }
              >
                {item.visible ? <VisibleIcon /> : <VisibleOffIcon />}
              </button>
              <button className='more' onClick={() => setShowMoreOptions((s) => !s)}>
                <ThreeDots />
                {showMoreOptions && (
                  <Tooltip setVisible={setShowMoreOptions}>
                    <div className='more-options'>
                      <button
                        onClick={() =>
                          handleEditItem({
                            type,
                            config: item.config,
                            created: false,
                            visible: true
                          })
                        }
                      >
                        <DuplicateIcon />
                        <p>Duplicate</p>
                      </button>
                      {!ConfiglessBlocks.includes(type) ? (
                        <button onClick={() => handleEditItem(item)}>
                          <PencilIcon />
                          <p>Edit</p>
                        </button>
                      ) : (
                        ''
                      )}
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          removeItem(id)
                        }}
                      >
                        <DeleteIcon />
                        <p>Delete</p>
                      </button>
                    </div>
                  </Tooltip>
                )}
              </button>
            </div>
          </div>
        )) ||
          ''}
        {viewContent && (
          <div className='item-block'>
            <BlockComponent updateBlock={updateBlock} content={item.content} config={item.config} item={item} removeItem={() => removeItem(id)} />
          </div>
        )}
      </div>
    </div>
  )
}

export const ReportGrid = ({
  items = [],
  addItem = (block) => {},
  updateBlock = (block) => {},
  removeItem = () => {},
  setItems = () => {},
  addIdToBlock = (block) => {},
  fetchBlockData = (type, config) => {},
  formatToDate = (date) => {},
  context,
  singleColumn,

  // Fetches form static form data (select distincts for dropdowns) - requested once on open of modal.
  getModalFormData = (formtype) => {}
}) => {
  const modalControls = useModalControls()
  const [modalBlock, setModalBlock] = useState({})
  const [active, setActive] = useState(null)

  const handleBlockAddOrEdit = useCallback(
    async (block) => {
      if (ConfiglessBlocks.includes(block.type)) {
        console.log('adding configless block')
        block = addIdToBlock(block)
        return addItem(block)
      }

      // lets add an item here and not on submit so the block has an id when the data gets updated from the api on render of the model

      // TODO: Errors when importing existing template.
      if (!block.created) {
        block = addIdToBlock(block)
      }

      setModalBlock(block)
      modalControls.open()
    },
    [addItem, modalControls, setModalBlock, addIdToBlock]
  )

  const getData = async (blocktype, config) => {
    const data = await fetchBlockData(blocktype, config)
    return data
  }

  const handleModalSubmit = (block) => {
    if (!block.created) {
      addItem(block)
    } else {
      updateBlock(block)
    }
    setModalBlock({})
  }

  const handleModalCloseAfter = () => {
    setModalBlock({})
  }

  function handleDragEnd(event) {
    const { active, over } = event
    setActive(null)
    if (active.id !== over.id) {
      setItems((state) => {
        const index = state.findIndex((i) => over.id === i.id)
        const activeIndex = state.findIndex((i) => active.id === i.id)

        return arrayMove(state, activeIndex, index)
      })
    }
  }
  function handleDragStart(event) {
    const active = items.find((i) => i.id === event.active.id)
    setActive(active)
  }

  return (
    <div className={`grid-wrapper ${singleColumn ? 'singleColumn' : ''}`}>
      <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} modifiers={[restrictToVerticalAxis]} collisionDetection={closestCenter}>
        <div className='grid dnd'>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((i) => {
              return (
                <Item
                  key={i.id}
                  id={i.id}
                  type={i.type}
                  item={i}
                  handleEditItem={handleBlockAddOrEdit}
                  updateBlock={updateBlock}
                  removeItem={removeItem}
                  dragging={active?.id === i.id}
                  defaultViewing={ConfiglessBlocks.includes(i.type)}
                />
              )
            })}
          </SortableContext>
          <DragOverlay>{active && <Item id={active.id} item={active} type={active.type} defaultViewing={false} />}</DragOverlay>
          <ReportBlockAdd config={{ addItem: handleBlockAddOrEdit }} />

          {modalBlock.type && (
            <ReportEditModal
              controls={modalControls}
              block={modalBlock}
              updateModalBlock={setModalBlock}
              getData={getData}
              getModalFormData={getModalFormData}
              updateBlock={updateBlock}
              onSubmit={handleModalSubmit}
              onAfterClose={handleModalCloseAfter}
              items={items}
              context={context}
            ></ReportEditModal>
          )}
        </div>
      </DndContext>
    </div>
  )
}
